import { useOrgData } from "../../../../../../contexts/OrgDataContext";
import { useCallback, useMemo, useState } from "react";
import Badge from "../../../../../../components/badges/Badge";
import { getText, LOGS_TYPES } from "../../../../../../services/log/log-service";
import moment from "moment/moment";
import { ExclamationTriangleIcon, InformationCircleIcon, TagIcon } from "@heroicons/react/20/solid";
import { formatSecondsToHMS } from "../../../../../../helpers/date/date-helper";

export const LISIPatternLog = ({ log }) => {
  const { eventTypes } = useOrgData();

  const [arePatternLogsVisible, setArePatternLogsVisible] = useState(false);

  const patternLogs = log.pattern_logs ?? [];
  const patternData = log.pattern_data.reduce((acc, { name, value }) => ({ ...acc, [name]: value }), {});

  const togglePatternLogs = () => setArePatternLogsVisible(!arePatternLogsVisible);

  const isOvenTimeTooShort = useCallback((pieceLog) => {
    return pieceLog?.data_oven_time < parseInt(patternData.oven_time)
  });
  const isOvenTimeTooLong = useCallback((pieceLog) => {
    return parseInt(patternData.oven_time) + parseInt(patternData.oven_tolerance) < pieceLog?.data_oven_time;
  });
  const _isPieceOk = useCallback((pieceLog) => {
    const isTransferTimeTooLong = parseInt(patternData.transfer_time) + parseInt(patternData.transfer_tolerance) < pieceLog?.data_transfer_time;
    return !isOvenTimeTooShort && !isOvenTimeTooLong && !isTransferTimeTooLong;
  });

  const patternExecutionTheoricalQuantity = log?.pattern_execution?.data?.quantity ?? 0;

  const piecesLogsOk = patternLogs.filter(_isPieceOk);

  return useMemo(
    () => (
      <div className="ml-8 border-l-1 border-perception-gray-600 relative flex pb-2 pr-4 cursor-pointer" onClick={togglePatternLogs}>

        {/* Circle */}
        <div className="absolute size-3 top-0 -left-1.5 rounded-full bg-perception-gray-500">
          <div className="size-2 bg-perception-blue absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"/>
        </div>

        {/* Datetime */}
        <div className="text-sm -mt-1 w-32 text-center">
          {moment(log.time).format('HH:mm:ss')}
        </div>

        {/* Pattern content */}
        <div className="flex-1 bg-perception-black-800 px-4 py-4 rounded-md relative overflow-hidden">
          <div className="bg-perception-blue w-1 absolute top-1.5 bottom-1.5 left-1.5 rounded-md"/>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4 ml-3">
              <TagIcon className="size-4 text-perception-blue"/>
              <div>
                {log.pattern_execution?.data?.of && `#${log.pattern_execution.data.of} - `}
                {log.pattern_name}
              </div>
              <Badge text={`${patternExecutionTheoricalQuantity} pièces`}/>
              {!log.pattern_execution?.finished && <Badge text="En cours" type="blue"/>}
            </div>
            {/* Complete tag */}
            {
              piecesLogsOk.length >= patternExecutionTheoricalQuantity ?
                <Badge text={`Conforme (${piecesLogsOk.length}/${patternExecutionTheoricalQuantity})`} type="green" /> :
                <Badge text={`Non conforme (${piecesLogsOk.length}/${patternExecutionTheoricalQuantity})`} type='yellow' />
            }
          </div>

          {arePatternLogsVisible &&
            <>
              <div className="ml-4 mt-2 text-sm text-perception-gray-600">
                Temps de chauffe
                : {formatSecondsToHMS(patternData.oven_time)} (±{formatSecondsToHMS(patternData.oven_tolerance)})
                -
                Temps de transfert
                : {formatSecondsToHMS(patternData.transfer_time)} (±{formatSecondsToHMS(patternData.transfer_tolerance)})
              </div>
              <div className="mt-4">
                {
                  patternLogs.map((patternLog, pieceLogIndex) => {
                    const isTransferTimeTooLong = parseInt(patternData.transfer_time) + parseInt(patternData.transfer_tolerance) < patternLog?.data_transfer_time;

                    const isAlert = patternLog.type === LOGS_TYPES.ALERT;

                    const circleColor = isAlert ? 'bg-perception-error-500' : !_isPieceOk(patternLog) ? 'bg-perception-warn-500' : 'bg-perception-black-900';
                    const textColor = isAlert ? 'text-perception-error-500 font-medium' : !_isPieceOk(patternLog) ? 'text-perception-warn-500 font-medium' : '';

                    return (
                      <div key={pieceLogIndex} className="ml-12 border-l-1 border-perception-gray-600 last:border-transparent relative flex pb-2 last:pb-0">

                        {/* Circle */}
                        <div className="absolute size-3 top-0 -left-1.5 rounded-full bg-perception-gray-500">
                          <div
                            className={`size-2 ${circleColor} absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full`} />
                        </div>

                        {/* Datetime */}
                        <div className="text-xs -mt-0.5 w-24 text-center">
                          {moment(patternLog.time).format('HH:mm:ss')}
                        </div>

                        {/* Log content */}
                        <div className="flex-1 bg-perception-black-800 rounded-md flex justify-between items-center text-sm -mt-1 ml-4 text-perception-gray-600">
                          <div className="flex items-center gap-4">
                            {
                              isAlert ?
                                <ExclamationTriangleIcon className={`size-4 min-w-4 ${textColor}`} />
                                :
                                <InformationCircleIcon className={`size-4 min-w-4 ${textColor}`} />
                            }
                            <div className={`text-sm ${textColor} flex gap-2`}>
                              {getText(patternLog, {}, true, eventTypes)}
                              {patternLog?.data_piece_id !== null && <Badge text={`#${patternLog?.data_piece_id}`} />}
                            </div>
                          </div>
                          {!isAlert &&
                            <div className="grid grid-cols-3 gap-2">
                              <Badge
                                text={`Chauffe : ${formatSecondsToHMS(patternLog?.data_oven_time) || '-'}`}
                                type={(isOvenTimeTooShort || isOvenTimeTooLong) ? 'yellow' : 'green'}
                              />
                              <Badge
                                text={`Four <> Frappe : ${formatSecondsToHMS(patternLog?.data_transfer_time) || '-'}`}
                                type={isTransferTimeTooLong ? 'yellow' : 'green'}
                              />
                              <Badge
                                text={`Frappe <> Découpe : ${formatSecondsToHMS(patternLog?.data_transfer_hit_cut_time) || '-'}`}
                                type="green"
                              />
                            </div>
                          }
                          {isAlert && <Badge text="Alerte levée" type="red" />}
                        </div>
                      </div>
                    )
                  })
                }
                {
                  patternLogs.length === 0 &&
                  <div className="ml-4 text-sm text-perception-gray-600">
                    Aucune donnée pour cette exécution.
                  </div>
                }
              </div>
            </>
          }
        </div>
      </div>
    ),
    [arePatternLogsVisible, log, patternLogs],
  );
};
